import ShoppingCart from '@c/ShoppingCart';
import { AddressDocument } from '@models/address';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { getItem, getItems, logEvent } from '@util/analytics';
import {
  calculateShipping,
  getCartItemsSubtotal,
  getCartState,
  getDefaultCart,
  getShippingRegion,
  logCart,
  setCartState,
} from '@util/firestore/cart';
import { getProductById } from '@util/firestore/products';
import { Rate } from '@util/firestore/shipengine';
import useRealTimeProducts from '@util/hooks/useRealtimeProducts';
import { getCartItemFromProductDocument } from '@util/index';
import { logError } from '@util/logError';
import { Cart, CartItem } from '@util/types/firestore/carts';
import { ProductDocument, Variation } from 'models/product';
import React, { createContext, ReactNode, useEffect, useState } from 'react';
import { useAttributionContext } from './AttributionContext';
import { useAuth } from './AuthContext';
import { useToastContext } from './ToastContext';
import { Coupon } from 'models/coupon';
import { PaymentDocument } from 'models/payment';
import {
  getShipping,
  matchCartItemVariationsToProductVariation,
} from '@util/firestore/cart/cart.service';
import { getTax } from '@util/firestore/taxjar';

type ShoppingCartProviderProps = {
  children: ReactNode;
};

type ShoppingCartContext = {
  addCoupons: (coupons: Coupon[]) => void;
  calculatingTax: boolean;
  canAddProductToCart: (
    productId: string,
    selectedVariations?: Variation[] | null
  ) => boolean;
  cart?: Cart | null; // cart is null when user is not logged in, undefined when loading in, and Cart when loaded
  cartOpen: boolean;
  clearCart: () => void;
  clearCartAndRedirect: (url: string) => Promise<void>;
  decreaseCartQty: (id: string) => void;
  getCartItemsSubtotal: (items: CartItem[]) => number;
  getItemCount: () => number;
  getItemQty: (id: string) => number;
  handleShippingRateSelected: ({
    product_id,
    rate,
  }: {
    product_id: string;
    rate: Rate;
  }) => void;
  increaseCartQty: (
    item: CartItem,
    selectedVariations?: Variation[] | null
  ) => void;
  realTimeProducts: ProductDocument[];
  removeRateIds: () => void;
  removeCoupon: (coupon: Coupon) => void;
  removeFromCart: (id: string) => void;
  setCalculatingTax: React.Dispatch<React.SetStateAction<boolean>>;
  setCartOpen: React.Dispatch<React.SetStateAction<boolean>>;
  updateCartItem: (item: CartItem) => void;
  updateDonationAmount: (amount: number) => void;
  updateShippingAddress: (
    address: AddressDocument | null,
    removeRateIds?: boolean
  ) => void;
  calculateTax: () => Promise<void>;
  toggleAffirmFee: (enabled: boolean) => void;
  updateRAFee: (ra_fee: number) => void;
  setGuestPaymentMethod: (paymentMethod: PaymentDocument | null) => void;
  guestPaymentMethod: PaymentDocument | null;
  cartIsFetching: boolean;
  setCartMutation: UseMutationResult<void, Error, { newCartState: Cart }>;
  guestOptedIn: boolean;
  setGuestOptedIn: (optedIn: boolean) => void;
};

const ShoppingCartContext = createContext({} as ShoppingCartContext);

export function useShoppingCart() {
  const shoppingCart = React.useContext(ShoppingCartContext);
  if (!shoppingCart) {
    throw new Error('CurrentUserContext: No value provided');
  }
  return shoppingCart;
}

export function ShoppingCartProvider({ children }: ShoppingCartProviderProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [guestPaymentMethod, setGuestPaymentMethod] =
    useState<PaymentDocument | null>(null);
  const [guestOptedIn, setGuestOptedIn] = useState(false);
  // Firestore Queries / Mutations.
  const { data: cart, isFetching: cartIsFetching } = useQuery({
    queryKey: ['cart', user?.uid],
    queryFn: () =>
      getCartState({
        uid: user?.uid ?? '',
      }),
    refetchOnWindowFocus: true,
  });
  const setCartMutation = useMutation({
    mutationFn: setCartState,
    onMutate: async ({ newCartState }) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['cart'] });

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData(['cart', user?.uid]) as
        | Cart
        | undefined;

      // Optimistically update to the new value
      queryClient.setQueryData(['cart'], (old: Cart | undefined) => ({
        ...(old && { old }),
        ...newCartState,
      }));

      // Return a context object with the snapshotted value
      return { previousCart };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, newCart, context) => {
      showErrorToast((err as Error).message);
      if (context) {
        logError(err);
        queryClient.setQueryData(['cart'], context.previousCart);
      }
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });

  // --------------------------------------------

  // Live object of products in cart
  // Memoize to prevent infinite loop when cart.product_ids is undefined
  const productIds = React.useMemo(
    () => cart?.product_ids ?? [],
    [cart?.product_ids]
  );
  const { realTimeProducts } = useRealTimeProducts(productIds);

  // CRUD operations on cart item quantities
  async function increaseCartQty(newItem: CartItem) {
    if (!user || !cart) return;
    try {
      const productDoc = await getProductById(newItem.product_id);

      if (!productDoc) {
        showErrorToast('Product not found');
        return;
      }

      if (productDoc.out_of_stock) {
        showErrorToast('Product is out of stock');
        return;
      }

      if (!productDoc.date_added) {
        showErrorToast('Product is not available for purchase');
        return;
      }

      const currItems = cart?.items ?? []; // initialize cart items if empty
      const index = currItems.findIndex(
        (currItem) => currItem.product_id === newItem.product_id
      );

      // if the item does not exist in the cart
      if (index === -1) {
        try {
          // Calculate new cart and update in firestore
          const newCart = calcCart({
            items: [...currItems, newItem],
          });
          await setCartMutation.mutateAsync({ newCartState: newCart });
          // notifyItemsAdded();
          // ------
          // Analytics
          logCart(newItem, productDoc, user.uid);
          // ------
        } catch (error) {
          showErrorToast('Failed to add item to cart');
          console.error('Error adding item:', error);
          return;
        }
      } else {
        // if the item already exists in the cart
        const cartItem = currItems[index];
        const newQty = cartItem.qty + newItem.qty;

        try {
          const product = realTimeProducts.find(
            (p) => p.id === newItem.product_id
          );

          if (!product) {
            showErrorToast('Product information not available');
            return;
          }

          let productStock = product.stock;

          if (product.has_variations) {
            const currentVariations = matchCartItemVariationsToProductVariation(
              {
                cartItem,
                product,
              }
            );
            if (currentVariations?.length) {
              productStock = Math.min(...currentVariations.map((v) => v.qty));
            }
          }

          if (product && newQty > productStock) {
            // if user tries to add more items than are in stock
            notifyError(productStock); // notify user
            return;
          }

          cartItem.qty = newQty; // update cart item quantity

          // Analytics
          logCart(newItem, productDoc, user.uid);
          // ------

          // Calculate new cart and update in firestore
          const newCart = calcCart({
            items: currItems,
          });
          await setCartMutation.mutateAsync({ newCartState: newCart });
          // ------
        } catch (error) {
          showErrorToast('Failed to update item quantity');
          console.error('Error updating quantity:', error);
          return;
        }
      }

      try {
        if (cart && !cartOpen) {
          logEvent('view_cart', {
            items: getItems(realTimeProducts),
            currency: 'USD',
            value: getCartItemsSubtotal(cart.items),
          });
        }
      } catch (error) {
        console.error('Error logging cart view:', error);
        // Non-critical error, don't show to user
      }
    } catch (error) {
      showErrorToast('An unexpected error occurred');
      console.error('Unexpected error in increaseCartQty:', error);
    }
  }

  function decreaseCartQty(id: string) {
    if (!cart) return;
    const currItems = cart.items;
    const index = currItems.findIndex((item) => item.product_id === id);
    if (index === -1) return;
    const item = currItems[index];
    if (item.qty === 1) {
      const filteredItems = currItems.filter((item) => item.product_id !== id);
      const product = realTimeProducts.find((p) => p.id === id);
      if (product) {
        logEvent('remove_from_cart', {
          currency: 'USD',
          items: [getItem(product)],
        });
      }
      const newCart = calcCart({
        items: filteredItems,
      });
      setCartMutation.mutate({ newCartState: newCart });
    } else {
      item.qty -= 1;
      const newCart = calcCart({
        items: currItems,
      });
      setCartMutation.mutate({ newCartState: newCart });
    }
  }
  function removeFromCart(id: string) {
    if (!user || !cart) return;
    const currItems = cart?.items ?? []; // initialize cart items if empty
    const filteredItems = currItems.filter((item) => item.product_id !== id);
    const newCart = calcCart({
      items: filteredItems,
    });
    setCartMutation.mutate({ newCartState: newCart });
  }
  // --------------------------------------------

  const { showErrorToast, showSuccessToast } = useToastContext();

  // Local State
  const [cartOpen, setCartOpen] = useState(false);
  const [calculatingTax, setCalculatingTax] = useState(false);
  // const [calculatingReturnInfo, setCalculatingReturnInfo] = useState(false);
  // --------------------------------------------

  // updates cart when realtime products change
  const { getAttribution } = useAttributionContext();

  useEffect(() => {
    if (!cart) return;
    let shouldUpdate = false;
    const notifications: ('' | 'removed' | 'updated')[] = [];
    const products = [...realTimeProducts];
    // 1. generate cart items based on most update to date product info
    let currCartItems = products
      .map((p) => {
        const currCartItem = cart.items.find((i) => i.product_id === p.id);
        if (!currCartItem) return;

        // Get all matching variations for this cart item
        const currVariations = matchCartItemVariationsToProductVariation({
          cartItem: currCartItem,
          product: p,
        });

        // If we have matching variations, use them to update the cart item
        const newCartItem = getCartItemFromProductDocument(
          p,
          currVariations,
          currCartItem.attribution,
          currCartItem.custom_inputs,
          currCartItem.qty
        );
        return {
          ...currCartItem,
          ...newCartItem,
        };

        // If no matching variations, just update the basic product info
      })
      .filter(Boolean) as CartItem[];
    // 2. check if any realtime products are out of stock
    const outOfStock = products.filter(
      (p) => p.out_of_stock && !p.bought_by?.includes(cart.uid)
    );
    if (outOfStock.length) {
      notifications.push('removed');
      shouldUpdate = true;
      // remove out of stock items from cart
      currCartItems = currCartItems.filter((i) => {
        const found = outOfStock.find((p) => p.id === i.product_id);
        return !found;
      });
    }

    // 3. check if any cart items have qty greater than stock
    const itemsWithQtyGreaterThanStock = currCartItems.filter((i) => {
      const product = products.find((p) => p.id === i.product_id);
      if (!product) return false;
      const variation = matchCartItemVariationsToProductVariation({
        cartItem: i,
        product,
      });
      if (variation?.length) {
        return i.qty > Math.min(...variation.map((v) => v.qty));
      }
      return i.qty > (product?.stock ?? 0);
    });
    if (itemsWithQtyGreaterThanStock.length) {
      shouldUpdate = true;
      // set cart item qty to stock
      currCartItems = currCartItems.map((i) => {
        const found = itemsWithQtyGreaterThanStock.find(
          (item) => item.product_id === i.product_id
        );
        if (!found) return i;
        const product = products.find((p) => p.id === i.product_id);
        if (!product) return i;
        const variation = matchCartItemVariationsToProductVariation({
          cartItem: i,
          product,
        });
        if (variation?.length) {
          i.qty = Math.min(...variation.map((v) => v.qty));
        } else if (i.qty > (product?.stock ?? 0)) {
          i.qty = product?.stock ?? 0;
        }
        return i;
      });
      currCartItems = currCartItems.filter((i) => i.qty > 0);
    }

    // 4. check if any cart items with size variations have zero qty
    const itemsWithSizes = currCartItems.filter((i) => i.size !== undefined);
    const variationsToRemove = itemsWithSizes
      .map((i) => {
        const product = products.find((p) => p.id === i.product_id);
        if (!product) return [];
        const variations = matchCartItemVariationsToProductVariation({
          cartItem: i,
          product,
        });
        if (!variations?.length)
          return [i.size?.letter, i.size?.number, i.color];
        const sizes: (string | number)[] = [];
        variations.forEach((v) => {
          if (v.qty <= 0) {
            sizes.push(v.size);
          }
        });
        return sizes;
      })
      .flat()
      .filter(Boolean) as (string | number)[];
    if (variationsToRemove.length) {
      shouldUpdate = true;
      notifications.push('removed');
      // remove items with zero qty from cart (even for gear combos. Maybe we only remove one of the sizes , but that adds complexity)
      currCartItems = currCartItems.filter((i) => {
        const size = i.size?.letter || i.size?.number;
        if (size) {
          return !variationsToRemove.includes(size);
        }
        return true;
      });
    }

    // 5. find cart items with shipping costs already selected
    const cartItemsWithShipping = cart.items.filter((i) => i.shipping_region);
    // check if any cart items with shipping costs need to update their shipping costs based on the latest product info
    const shippingUpdates = cartItemsWithShipping
      .map((i) => {
        const found = products.find((p) => p.id === i.product_id);
        if (!found) {
          return;
        }
        if (!found.is_flat_rate) return;
        const shippingRate = calculateShipping(
          found,
          i.shipping_region ?? 'US'
        );
        if (!shippingRate) {
          //  remove shipping cost if it is no longer available
          return {
            shipping_cost: undefined,
            shipping_region: undefined,
            item_id: i.product_id,
          };
        }
        if (i.shipping_cost !== shippingRate.cost) {
          return {
            shipping_cost: shippingRate.cost,
            shipping_region: shippingRate.code,
            item_id: i.product_id,
          };
        }
      })
      .filter(Boolean) as {
      shipping_cost: number | undefined;
      shipping_region: string | undefined;
      item_id: string;
    }[];
    if (shippingUpdates.length) {
      notifications.push('updated');
      shouldUpdate = true;
    }
    // for each shipping update, update the cart item
    shippingUpdates.forEach((update) => {
      currCartItems = currCartItems.map((i) => {
        if (!update.shipping_cost) {
          // remove shipping cost if it is no longer available
          delete i.shipping_cost;
          delete i.shipping_region;
          return i;
        }
        if (i.product_id === update.item_id) {
          return {
            ...i,
            shipping_cost: update.shipping_cost,
            shipping_region: update.shipping_region,
          };
        }
        return i;
      });
    });

    // // 6. check if other keys have changed, this could include cost, title, slug, etc.
    function areKeysDifferent(obj1: any, obj2: any) {
      // Helper function to determine if a value is primitive.
      // Arrays and non-null objects are not considered primitive.
      const isPrimitive = (value: any) =>
        value === null || typeof value !== 'object';

      // Filter keys to only include those with primitive values.
      const filteredKeys1 = Object.keys(obj1).filter((key) =>
        isPrimitive(obj1[key])
      );
      const filteredKeys2 = Object.keys(obj2).filter((key) =>
        isPrimitive(obj2[key])
      );

      // Sort keys to ensure order-independent comparison.
      const sortedKeys1 = filteredKeys1.sort();
      const sortedKeys2 = filteredKeys2.sort();

      // If the filtered key counts differ, the keys differ.
      if (sortedKeys1.length !== sortedKeys2.length) {
        return true;
      }

      // Check each key for equality.
      for (let i = 0; i < sortedKeys1.length; i++) {
        if (obj1[sortedKeys1[i]] !== obj2[sortedKeys2[i]]) {
          return true;
        }
      }

      return false;
    }
    const diff = currCartItems.map((i) => {
      return areKeysDifferent(
        i,
        cart.items.find((c) => c.product_id === i.product_id)
      );
    });
    if (diff.some((d) => d)) {
      notifications.push('updated');
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      const newCart = calcCart({
        items: currCartItems,
      });
      setCartMutation.mutate({
        newCartState: newCart,
      });
      notifications.forEach((n) => {
        if (n === 'removed')
          showErrorToast('An item in your cart has been removed');
        if (n === 'updated')
          showSuccessToast('An item in your cart has been updated');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realTimeProducts]);
  // #region cart interactions
  function getItemCount() {
    if (!cart) return 0;
    const total = cart.items.reduce(
      (accumulator, cartItem) => accumulator + cartItem.qty,
      0
    );
    return total;
  }

  function getItemQty(id: string) {
    if (!cart) return 0;
    return cart.items.find((item) => item.product_id === id)?.qty || 0;
  }

  function addCoupons(coupons: Coupon[]) {
    const currCart = cart;
    if (!currCart) return;
    const newCart = calcCart({
      ...currCart,
      coupons,
    });
    setCartMutation.mutate({ newCartState: newCart });

    queryClient.invalidateQueries({ queryKey: ['cart', user?.uid] });
  }

  function removeCoupon(coupon: Coupon) {
    const currCart = cart;
    if (!currCart) return;

    const newCart = calcCart({
      ...currCart,
      coupons: currCart.coupons?.filter((c) => c.code !== coupon.code) ?? [],
    });
    setCartMutation.mutate({ newCartState: newCart });
  }

  function handleShippingRateSelected({
    product_id,
    rate,
  }: {
    product_id: string;
    rate: Rate;
  }) {
    const currCart = cart;
    if (!currCart || !currCart.shipping_address) return;
    const index = currCart.items.findIndex((i) => i.product_id === product_id);
    if (index === -1) return;
    const newItems = [...currCart.items];
    newItems[index].shipping_cost = rate.total_amount;
    newItems[index].shipping_region = getShippingRegion(
      currCart.shipping_address
    );
    newItems[index].rate_id = rate.rate_id;
    newItems[index].service_type = rate.service_type;
    const newCart = calcCart({
      ...currCart,
      items: newItems,
    });
    setCartMutation.mutate({ newCartState: newCart });
  }

  function updateShippingAddress(
    address: AddressDocument | null,
    removeRateIds = false
  ) {
    if (!cart) return;
    if (address === null) delete cart.shipping_address;
    else {
      // if any items have rate ids, remove them
      if (removeRateIds) {
        queryClient.resetQueries({ queryKey: ['rates'] });
        cart.items.forEach((item) => {
          if (item.rate_id) {
            delete item.rate_id;
            delete item.service_type;
            delete item.shipping_cost;
            delete item.shipping_region;
          }
        });
      }
      const newCart = calcCart({
        items: cart.items,
        shipping_address: address,
      });
      setCartMutation.mutate({ newCartState: newCart });
    }
  }

  async function calculateTax() {
    if (calculatingTax) return;
    try {
      setCalculatingTax(true);
      if (!cart) return;
      const items = cart.items;
      const promises = items.map((item) => {
        if (!cart.shipping_address) return;
        return getTax({
          product_id: item.product_id,
          seller_id: item.seller_id,
          to_address: cart.shipping_address,
          total: item.product_cost * item.qty,
          shipping: item.shipping_cost ?? 0,
        });
      });
      const taxes = await Promise.all(promises);
      let tax = 0;
      taxes.forEach((t) => {
        if (!t) return;
        const item = items.find((i) => i.product_id === t.product_id);
        if (!item) return;
        item.tax = t.amount;
        tax += t.amount;
      });
      const newCart = calcCart({
        items,
        tax,
      });
      setCartMutation.mutate({ newCartState: newCart });
    } catch (error) {
      // setError((error as Error).message);
    } finally {
      setCalculatingTax(false);
    }
  }

  function updateDonationAmount(amount: number) {
    if (!cart) return;
    const newCart = calcCart({
      donation_amount: amount,
    });
    setCartMutation.mutate({ newCartState: newCart });
  }
  function clearCart() {
    if (!user) return;
    setCartMutation.mutate({ newCartState: getDefaultCart(user.uid) });
  }
  function calcCart(updates: Partial<Cart>): Cart {
    const newCart: Cart = {
      ...(cart ?? getDefaultCart(user?.uid ?? '')),
      ...updates,
    };
    if (!newCart.items) return newCart;
    if (newCart.items.length === 0 && newCart.ra_fee) {
      newCart.ra_fee = 0;
    }
    const subtotal = getCartItemsSubtotal(newCart.items, newCart.coupons);
    const { shippingTotal } = getShipping({
      items: newCart.items,
      products: realTimeProducts,
      shipping_address: newCart.shipping_address,
    });
    const tax = newCart.items.reduce((acc, item) => acc + (item.tax ?? 0), 0);
    const product_ids = newCart.items.map((i) => i.product_id);
    const last_updated = Date.now();
    const total_after_tax =
      subtotal +
      shippingTotal +
      tax +
      (newCart.ra_fee ?? 0) +
      (newCart.donation_amount ?? 0) +
      (newCart.affirm_fee ?? 0);
    const total = Math.max(total_after_tax, 1);
    return {
      ...newCart,
      last_updated,
      product_ids,
      shipping: shippingTotal,
      subtotal,
      total,
      uid: newCart.uid,
      items: newCart.items,
      tax,
      ...(typeof newCart.donation_amount === 'number' && {
        donation_amount: newCart.donation_amount,
      }),
      ...(newCart.coupons && { coupons: newCart.coupons }),
      ...(newCart.created && { created: newCart.created }),
      ...(newCart.shipping_address && {
        shipping_address: newCart.shipping_address,
      }),
    };
  }

  async function clearCartAndRedirect(url: string) {
    if (!user) return;
    await setCartState({ newCartState: getDefaultCart(user.uid) });
    window.location.href = url;
  }

  // function getQtyOfVariationInStock(
  //   product: ProductDocument,
  //   selectedVariations?: Variation[] | null
  // ): number {
  //   if (selectedVariations && selectedVariations.length) {
  //     if (product.category2 === 'Gear Combos') {
  //       const qty1 =
  //         product.variations?.find(
  //           (v) =>
  //             (v.is_number && v.size === selectedVariations?.[0]?.size) ||
  //             v.size === selectedVariations?.[1]?.size
  //         )?.qty ?? 0;
  //       const qty2 =
  //         product.variations?.find(
  //           (v) =>
  //             (!v.is_number && v.size === selectedVariations?.[0]?.size) ||
  //             v.size === selectedVariations?.[1]?.size
  //         )?.qty ?? 0;
  //       return qty1 && qty2 ? Math.min(qty1, qty2) : qty1 || qty2;
  //     }
  //     let qty = 0;
  //     const letter = selectedVariations.find((v) => !v.is_number && v.size);
  //     const number = selectedVariations.find((v) => v.is_number && v.size);
  //     const color = selectedVariations.find((v) => v.color);
  //     product.variations?.forEach((v) => {
  //       if (
  //         (v.size === letter?.size || !letter) &&
  //         (v.size === number?.size || !number) &&
  //         (v.color === color?.color || !color)
  //       ) {
  //         qty += v.qty ?? 0;
  //       }
  //     });

  //     return qty;
  //   } else {
  //     return product.stock ?? 0;
  //   }
  // }

  function canAddProductToCart(
    productId: string,
    selectedVariations?: Variation[] | null
  ) {
    const product = realTimeProducts.find((p) => p.id === productId);
    if (!product) {
      return true; // if product is not found, assume it can be added
    }
    const cartItem = cart?.items.find((i) => i.product_id === productId);
    if (!cartItem) {
      return true;
    }

    const canAdd = cartItem.qty <= product.stock;
    return canAdd;
  }

  function updateCartItem(item: CartItem) {
    const currCart = cart;
    if (!currCart) return;
    const index = currCart.items.findIndex(
      (i) => i.product_id === item.product_id
    );
    if (index === -1) return;
    currCart.items[index] = item;
    const newCart = calcCart({
      ...currCart,
    });
    setCartMutation.mutate({ newCartState: newCart });
  }

  function removeRateIds() {
    if (!cart) return;
    queryClient.resetQueries({ queryKey: ['rates'] });
    const newItems = [...cart.items];
    newItems.forEach((item) => {
      if (item.rate_id) {
        delete item.rate_id;
        delete item.service_type;
        delete item.shipping_cost;
        delete item.shipping_region;
      }
    });
    const newCart = calcCart({
      items: newItems,
    });
    setCartMutation.mutate({ newCartState: newCart });
  }

  function toggleAffirmFee(enabled: boolean) {
    if (!cart) return;
    if (enabled && cart.affirm_fee) return;
    if (!enabled && !cart.affirm_fee) return;
    const affirm_fee = enabled ? cart.total * 0.03 : 0;
    const newCart = calcCart({
      affirm_fee,
    });
    setCartMutation.mutate({ newCartState: newCart });
  }

  function updateRAFee(ra_fee: number) {
    if (!cart) return;
    const newCart = calcCart({
      ra_fee,
    });
    setCartMutation.mutate({ newCartState: newCart });
  }

  // #endregion cart interactions

  function notifyError(qty: number) {
    const message =
      qty === 0
        ? 'This item is out of stock.'
        : qty === 1
        ? 'There is only 1 available of this item.'
        : `There are only ${qty} available of this item.`;
    showErrorToast(message);
  }

  return (
    <ShoppingCartContext.Provider
      value={{
        addCoupons,
        canAddProductToCart,
        cart,
        cartOpen,
        clearCart,
        clearCartAndRedirect,
        decreaseCartQty,
        getItemCount,
        getItemQty,
        getCartItemsSubtotal,
        handleShippingRateSelected,
        increaseCartQty,
        realTimeProducts,
        removeRateIds,
        removeCoupon,
        removeFromCart,
        setCartOpen,
        calculatingTax,
        setCalculatingTax,
        updateShippingAddress,
        updateCartItem,
        calculateTax,
        updateDonationAmount,
        updateRAFee,
        toggleAffirmFee,
        guestPaymentMethod,
        setGuestPaymentMethod,
        cartIsFetching,
        setCartMutation,
        guestOptedIn,
        setGuestOptedIn,
      }}
    >
      <ShoppingCart />
      {children}
    </ShoppingCartContext.Provider>
  );
}
